import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api";
import Seal from "@/components/Seal";
import BrandBanner from "@/components/BrandBanner";
import { toast } from "sonner";
import { Loader2, MailCheck, RotateCw } from "lucide-react";

const Signup = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [expiresHours, setExpiresHours] = useState(24);
  const [resending, setResending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await signup(form);
      if (res?.expires_hours) setExpiresHours(res.expires_hours);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await authApi.resendVerification(form.email);
      toast.success("Verification email sent again");
    } catch {
      toast.error("Could not resend right now");
    } finally { setResending(false); }
  };

  if (sent) {
    return (
      <div className="wechr-container flex min-h-[70vh] flex-col items-center justify-center py-12">
        <BrandBanner className="mb-6 w-full max-w-2xl" />
        <Card className="w-full max-w-md" data-testid="signup-verify-notice">
          <CardHeader className="items-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-wgreen-100"><MailCheck className="h-7 w-7 text-wgreen-700" /></div>
            <CardTitle className="mt-3 font-heading text-xl">Verify your email</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-navy-800">We've sent a verification link to <strong className="break-all">{form.email}</strong>. Click the link in that email to activate your account.</p>
            <p className="mt-2 text-sm text-[var(--wechr-muted-text)]">You must verify your email before you can sign in, submit applications, or upload documents.</p>
            <div className="mt-3 rounded-lg bg-[var(--wechr-subtle)] px-3 py-2 text-xs font-medium text-navy-800" data-testid="signup-expiry-notice">
              For your security, this link is valid for {expiresHours} hours. If it expires, just resend below.
            </div>
            <div className="mt-6 space-y-2">
              <Button variant="outline" className="w-full" onClick={resend} disabled={resending} data-testid="signup-resend-button">
                {resending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCw className="mr-2 h-4 w-4" />} Resend verification email
              </Button>
              <Button className="btn-green w-full" onClick={() => navigate("/login")} data-testid="signup-go-login-button">Go to sign in</Button>
            </div>
            <p className="mt-4 text-xs text-[var(--wechr-muted-text)]">Didn't get it? Check your spam folder, or resend above.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="wechr-container flex min-h-[70vh] flex-col items-center justify-center py-12">
      <BrandBanner className="mb-6 w-full max-w-2xl" />
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <Seal size={40} />
          <CardTitle className="mt-2 font-heading text-xl">Create your WECH-RB account</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div><Label htmlFor="name">Full name</Label><Input id="name" required value={form.name} onChange={set("name")} data-testid="signup-name-input" className="mt-1" /></div>
            <div><Label htmlFor="company">Company name</Label><Input id="company" value={form.company_name} onChange={set("company_name")} data-testid="signup-company-input" className="mt-1" /></div>
            <div><Label htmlFor="email">Email</Label><Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="signup-email-input" className="mt-1" /></div>
            <div><Label htmlFor="password">Password</Label><Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="signup-password-input" className="mt-1" /></div>
            <Button type="submit" className="btn-green w-full" disabled={loading} data-testid="signup-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
            </Button>
          </form>
          <div className="my-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-[var(--wechr-border)]" />
            <span className="text-xs text-[var(--wechr-muted-text)]">or</span>
            <div className="h-px flex-1 bg-[var(--wechr-border)]" />
          </div>
          <Button type="button" variant="outline" className="w-full" onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/dashboard";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }} data-testid="google-signup-button">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.2l7.9 6.1C12.2 13.2 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-2.8-.4-4H24v7.6h12.6c-.3 2.1-1.6 5.2-4.7 7.3l7.2 5.6c4.3-4 6.8-9.9 6.8-16.5z"/><path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.8l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.5l-7.2-5.6c-2 1.4-4.6 2.3-7.8 2.3-6.4 0-11.8-3.7-13.6-9.8l-7.9 6.1C6.4 42.6 14.6 48 24 48z"/></svg>
            Continue with Google
          </Button>
          <p className="mt-4 text-center text-sm text-[var(--wechr-muted-text)]">
            Already have an account? <Link to="/login" className="font-medium text-wgreen-700 hover:underline">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Signup;
